/****************************************************
 * Module Name:  offer.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState } from "react";
import { DataGridPro, GridToolbarExport, GridToolbar, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, Tooltip, Typography, Stack, Menu, MenuItem, } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { useEffect } from "react";
import Select from "react-select";
import CircularProgress from "@mui/material/CircularProgress";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import StoneDetailModal from "../common/stoneDetailModal";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import CommonSnackbar from "../common/commonSnackBar";
import { LoadingButton } from "@mui/lab";
import OfferProxy from "../../utility/proxy/manager/offerProxy";
import renderCellExpand from "../common/dataGridCellExpand";
import VideoLibraryTwoToneIcon from '@mui/icons-material/VideoLibraryTwoTone';
import StoneMediaModal from "../common/stoneMediaModal";
import PacketHistoryModal from "../common/packetHistoryModal";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PageTitle from "../common/pageTitle";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UserDetailModal from "../operations/userDetailModal";
import { styled, alpha } from "@mui/material/styles";
import Download from "@mui/icons-material/Download";
import { createNestedExcelFile } from "../common/createNestedExcel";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { TbTableExport } from "react-icons/tb";

const StyledMenuButtons = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 100,
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55, 65, 81)'
        : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      fontSize: 15,
      padding: "4px 12px",
      '& .MuiSvgIcon-root': {
        fontSize: 16,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

const Offer = () => {
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("");
  const [customerData, setCustomerData] = useState({
    customer: "",
    customerOptions: [],
  });
  const [listData, setListData] = useState([]);
  const [mainData, setMainData] = useState([]);
  const [offerLoader, setOfferLoader] = useState(false);
  const [fetchData, setFetchData] = useState(false);
  const [stoneId, setStoneId] = useState("");
  const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
  const [columnNames, setColumnNames] = React.useState([]);
  const [filterValues, setFilterValues] = useState({});

  const [stoneMediaURL, setStoneMediaURL] = useState([]);
  const [showStoneMediaModal, setShowStoneMediaModal] = useState(false);
  const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
  const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
  const [packetId, setPacketId] = useState("");
  const [kapanId, setKapanId] = useState("");
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);

  // eslint-disable-next-line
  const [local_Permission, setLocal_Permission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.OPERATION_OFFER
    )
  );

  // eslint-disable-next-line
  const [fullExcelPermission, setFullExcelPermission] = useState(
    Constants.USER_PERMISSION.filter(
      (iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.DIRECT_EXCEL_DOWNLOAD_FULL
    )
  );

  useEffect(() => {
    PageTitle("OPERATION_OFFER_OFFERWISE");
    getAllCustomerData();
  }, []);

  const getAllCustomerData = () => {
    OfferProxy.GetCustomersCreatedOffer(
      local_Permission.length > 0 ? -1 : Constants.loggedInUser.id,
      Constants.loggedInUser.usertype, (response) => {
        prepareUserData(response);
      });
  };

  const prepareUserData = (response) => {

    let optionsArray = [];
    optionsArray.push({ value: "*", label: "-- ANY --" });
    response.map((data) => {
      let option = {
        value: data.id,
        label: data.companyname,
      };
      optionsArray.push(option);
      return true;
    });

    setCustomerData({
      customerOptions: optionsArray,
      customer: optionsArray.length > 0 ? [optionsArray[0]] : []
    });
    setFetchData(true);

    return true;
  };

  useEffect(() => {
    if (fetchData) {
      if (customerData.customer) {
        handleSearch();
      }
    }
  }, [fetchData]);

  const handleSearch = () => {
    let customerList = Constants.loggedInUser.usertype === "C"
      ? [Constants.loggedInUser.id]
      : fullOptionToValue(customerData.customer);
    setOfferLoader(true);
    OfferProxy.GetOfferDetailForCustomer(
      local_Permission.length > 0 ? -1 : Constants.loggedInUser.id,
      Constants.loggedInUser.usertype,
      customerList, (response) => {
        if (response) {
          prepareColumns();
          const uniqueFilterValues = {
            customercompanyname: Utility.getDistinctOptions(response, "customerid"),
            broker: Utility.getDistinctOptions(response, "brokerid"),
            createdby: Utility.getDistinctOptions(response, "employeeid"),
          };
          const combinedFilterValues = {
            customercompanyname: uniqueFilterValues.customercompanyname.map(value => {
              const matchingItem = response.find(item => item.customerid === value);
              return matchingItem ? `${value} | ${matchingItem.customercompanyname}` : [];
            }),
            broker: uniqueFilterValues.broker.map(value => {
              const matchingItem = response.find(item => item.brokerid === value);
              if (matchingItem) {
                let displayValue = matchingItem.brokerid === -1 ? " - " : `${matchingItem.brokerid} | ${matchingItem.brokercompanyname}`;
                return displayValue;
              }
              return [];
            }),
            createdby: uniqueFilterValues.createdby.map(value => {
              const matchingItem = response.find(item => item.employeeid === value);
              return matchingItem ? `${value} | ${matchingItem.employeename}` : [];
            }),
          };
          setFilterValues(combinedFilterValues);
          setListData(response);
          handleArray(response);
          setOfferLoader(false);
          setFetchData(false);
        } else {
          setOfferLoader(false);
          setShowSuccessSnackbar(true);
          setSuccessMessage(Constants.APiFailErrorMessage);
          setSnackbarVariant("error");
        }
      });
  };

  const getExcelData = (fullExcel) => {

    let data = listData;

    let stoneDataMap = new Map();
    listData.forEach(iData => {
      stoneDataMap.set(iData.stoneid, iData.offerCustomerDetailsList);
    });

    let excelDataOuter = [];
    let excelDataInner = new Map();

    data.forEach(iData => {


      excelDataOuter.push({
        ID: iData.offerid,
        CUSTOMER_COMPANY: iData.customername,
        BROKER_COMPANY: iData.brokername,
        CREATED_BY: iData.employeename,
        DATE: Utility.ConvertUtcToLocalDate(iData.createdat),
        TOTAL_CTS: iData.totalSize,
        AVG_PRICE: iData.avgPrice,
        AVG_DIS: iData.avgDiscount,
        TOTAL_PRICE: iData.totalPrice,
        REMARK: iData.remark,
      });

      let innerdata = iData.offerDetailedDTOList;
      let innerDataArray = [];
      innerdata.forEach(item => {

        innerDataArray.push({
          REPORT: item.certificateid,
          SHAPE: item.shape,
          ID: item.stoneid,
          CTS: item.size,
          COL: item.colortype === "WHITE" ? item.color : item.colordescriptions,
          CLA: item.clarity,
          CUT: item.cut ? item.cut : " ",
          POL: item.pol,
          SYM: item.sym,
          FLR: item.flo,
          MEASUREMENTS: item.measurements,
          DEP: !item.depthpercent ? "" : (Number(item.depthpercent)).toFixed(2),
          "TABLE%": !item.tablepercent ? "" : (Number(item.tablepercent)).toFixed(2),
          "CR ANG": !item.crownangle ? "" : (Number(item.crownangle)).toFixed(2),
          LAB: item.lab,
          RAP: item.rapnetprice,
          DIS: item.stonetype === "N" ? parseFloat((Number(item.discount)).toFixed(2)) : parseFloat(item.discount),
          PPC: parseFloat((Number(item.price)).toFixed(2)),
          TOTAL: parseFloat((Number(item.total)).toFixed(2)),
          LOCATION: item.location,
          STATUS: item.status,
          KEYTOSYMBOLS: item.keytosymbols ? item.keytosymbols : " ",
          MEDIA: item.stonemediaurl,
          status: item.status ? item.status : "",
          holdstonecustomerid: item.holdstonecustomerid,
          holdstoneuserid: item.holdstoneuserid,
          holdstonebrokerid: item.holdstonebrokerid,
          TABLEINC: item.tableinc ? item.tableinc : "",
          SIDEINC: item.sideinc ? item.sideinc : "",
          H_AND_A: item.h_and_a ? item.h_and_a : "",
          EYECLEAN: item.eyeclean ? item.eyeclean : "",
          TINGE: item.tinge ? item.tinge : "",
          MILKY: item.milky ? item.milky : "",
          OPEN: item.open ? item.open : "",
          LUSTER: item.luster ? item.luster : "",
          TABLEBLACK: item.tableblack ? "YES" : "NO",
          SIDEBLACK: item.sideblack ? "YES" : "NO",
          stonetype: item.stonetype,
          ORIGINAL_RAP: item.rapnetprice,
          OFFER_RAP: item.offerrapnetprice,
          ORIGINAL_DIS: item.originaldiscount,
          OFFER_DIS: item.offerdiscount,
          ORIGINAL_PCT: item.originalprice,
          OFFER_PCT: item.offerppc,
          ORIGINAL_TOTAL: item.originaltotal,
          OFFER_TOTAL: item.offertotal,
          REMARK: item.remark,
        });

      });

      excelDataInner.set(iData.offerid, innerDataArray);
    });

    const outer_table_excel_columns = [
      { label: "OFFER ID", key: "ID", width: 15 },
      { label: "CREATED BY", key: "CREATED_BY", width: 40 },
      { label: "BROKER ", key: "BROKER_COMPANY", width: 40 },
      { label: "CUSTOMER COMPANY", key: "CUSTOMER_COMPANY", width: 18 },
      { label: "DATE", key: "DATE", width: 14 },
      { label: "TOTAL CTS", key: "TOTAL_CTS", width: 14 },
      { label: "AVG PRICE", key: "AVG_PRICE", width: 14 },
      { label: "AVG DIS", key: "AVG_DIS", width: 14 },
      { label: "TOTAL PRICE", key: "TOTAL_PRICE", width: 18 },
      { label: "REMARK", key: "REMARK", width: 25 }
    ];

    const extraColumns = [
      { label: "ORG RAP", key: "ORIGINAL_RAP", width: 16 },
      { label: "OFFER RAP", key: "OFFER_RAP", width: 14 },
      { label: "ORG DIS", key: "ORIGINAL_DIS", width: 16 },
      { label: "OFFER DIS", key: "OFFER_DIS", width: 14 },
      { label: "ORG P/CT", key: "ORIGINAL_PCT", width: 16 },
      { label: "OFFER P/CT", key: "OFFER_PCT", width: 14 },
      { label: "ORG TOTAL", key: "ORIGINAL_TOTAL", width: 18 },
      { label: "OFFER TOTAL", key: "OFFER_TOTAL", width: 16 },
    ];

    const inner_table_excel_columns = fullExcel
      ? [...Constants.STOCK_EXCEL_COLUMNS, ...Constants.EXCEL_OPTIONAL_COLUMNS]
      : [...Constants.STOCK_EXCEL_COLUMNS];

    // swaping positions
    inner_table_excel_columns.splice(15, 4);     // removing columns  rap,dis,ppc,total
    inner_table_excel_columns.splice(10, 0, ...extraColumns);

    let startInnerTableColumnFrom = 1;

    createNestedExcelFile(excelDataOuter, excelDataInner, outer_table_excel_columns, inner_table_excel_columns, "PI-Offer.xlsx", startInnerTableColumnFrom);
  }

  const fullOptionToValue = (selectedData) => {
    let newArr = [];
    selectedData.map((data) => {
      if (data.value === "*") {
        return customerData.customerOptions.map((option) => {
          if (option.value !== "*") {
            newArr.push(option.value);
            return true;
          }
          return true;
        });
      }
      return true;
    })
    return Utility.GetValueArrayWithStarReplace(selectedData, newArr);
  };
  const prepareColumns = () => {
    let columnNames = [];

    Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
      if (columnsData.hasOwnProperty(iColPermission)) {
        const columnData = columnsData[iColPermission];
        columnNames.push(columnData);
        if (columnData.field === "rapnetprice") {
          columnNames.push({
            field: "offerrapnetprice",
            headerName: "OFFER RAP",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 120,
            renderCell: (params) => params.row.offerrapnetprice
          })
        }
        else if (columnData.field === "originaldiscount") {
          columnNames.push({
            field: "offerdiscount",
            headerName: "OFFER DIS",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.offerdiscount)
          })
        }
        else if (columnData.field === "originalprice") {
          columnNames.push({
            field: "offerprice",
            headerName: "OFFER P/CT",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.offerprice)
          })
        }
        else if (columnData.field === "originaltotal") {
          columnNames.push({
            field: "offertotal",
            headerName: "OFFER TOTAL",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.offertotal)
          })
        }
      }
    });

    setColumnNames(columnNames);
  }

  const handleArray = (lListData) => {
    let newMainData = {};
    if (lListData.length > 0) {
      lListData.map((list) => {
        let offerStones = []
        list.offerDetailedDTOList.map((stone) =>
          offerStones.push({
            offerID: stone.offerid,
            stoneid: stone.stoneid,
            stoneprice: stone.stoneprice,
            originaldiscount: stone.originaldiscount,
            offerdiscount: stone.offerdiscount,
            originalprice: stone.originalprice,
            offerprice: stone.offerppc,
            originaltotal: stone.originaltotal,
            offertotal: stone.offertotal,
            clarity: stone.clarity,
            color: stone.color,
            country: stone.country,
            cut: stone.cut,
            flo: stone.flo,
            location: stone.location,
            pol: stone.pol,
            sym: stone.sym,
            shape: stone.shape,
            size: stone.size,
            offerrapnetprice: stone.offerrapnetprice,
            rapnetprice: stone.rapnetprice,
            lab: stone.lab,
            certificateid: stone.certificateid,
            age: stone.age,
            stonetype: stone.stonetype,
            measurements: stone.measurements,
            depth: stone.depth,
            quality: stone.quality,
            shade: stone.shade,
            createdat: stone.createdat,
            height: stone.height,
            width: stone.width,
            certificateurl: stone.certificateurl,
            status: stone.status,
            kapanid: stone.kapanid,
            packetid: stone.packetid,
            laserinscription: stone.laserinscription,
            culetsize: stone.culetsize,
            paviliondepth: stone.paviliondepth,
            lrhalf: stone.lrhalf,
            tablepercent: stone.tablepercent,
            comment: stone.comment,
            fancycolor1: stone.fancycolor1,
            length: stone.length,
            tinge: stone.tinge,
            crownangle: stone.crownangle,
            milky: stone.milky,
            fancycolorintensity: stone.fancycolorintensity,
            fancycolorovertone: stone.fancycolorovertone,
            intensitylongtitle: stone.intensitylongtitle,
            pavilionangle: stone.pavilionangle,
            open: stone.open,
            note: stone.note,
            strln: stone.strln,
            culetcondition: stone.culetcondition,
            keytosymbols: stone.keytosymbols,
            crownheight: stone.crownheight,
            gridlemin: stone.gridlemin,
            h_and_a: stone.h_and_a,
            white: stone.white,
            eyeclean: stone.eyeclean,
            gridlepercent: stone.gridlepercent,
            boxid: stone.boxid,
            tableinc: stone.tableinc,
            sideinc: stone.sideinc,
            luster: stone.luster,
            tableblack: stone.tableblack,
            sideblack: stone.sideblack,
            stonemediaurl: stone.stonemediaurl,
          })
        );
        newMainData[list.offerid] = offerStones;
        return true;
      });
    }
    setMainData(newMainData);
  };

  const handleStoneDetailModal = (params) => {
    setStoneId(params.value);
    setShowStoneDetailModal(true);
  };

  const handlePacketDetailModal = (params, kapanid) => {
    setPacketId(params);
    setKapanId(kapanid);
    setShowPacketDetailModal(true);
  };

  const handleClose = () => {
    setShowStoneDetailModal(false);
    setShowPacketDetailModal(false);
    setShowStoneMediaModal(false);
    setShowSuccessSnackbar(false);
  };

  const columnsData = {
    "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
      field: "stoneid",
      headerName: "STONEID",
      headerAlign: "center",
      width: 80,
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
        <Button
          sx={{ textDecorationLine: "underline" }}
          onClick={() => handleStoneDetailModal(params)}
        >
          {params.value}
        </Button>
        :
        <div>{params.value}</div>,
    },
    "e24542ae-7b98-44f2-82be-09dfa2dd850b": {
      field: "mumid",
      headerName: "ID",
      headerAlign: "center",
      align: "center",
      width: 90,
      renderCell: (params) => (!params.value) ? "" : params.value,
    },
    "7a913460-41e0-441d-85b9-1f33d71416fb": {
      field: "suratid",
      headerName: "REF NO",
      headerAlign: "center",
      align: "center",
      width: 90,
      renderCell: (params) => (!params.value) ? "" : params.value,
    },
    "9dfe5dd6-726c-465b-9f61-22fca6660b4f": {
      field: "kapanid",
      headerName: "KAPANID",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
        <Button
          sx={{
            textDecorationLine: "underline",
            color: 'black',
          }}
          onClick={() => {
            setKapanId(params.row.kapanid);
            setShowKapanDetailModal(true);
          }}
        >
          {params.value}
        </Button>
        :
        <div>{params.value}</div>,
      width: 80,
    },
    "8a492e00-6167-45de-b078-e572e47c8a39": {
      field: "packetid",
      headerName: "PACKETID",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 80,
      renderCell: (params) => {
        if (params.value === null || params.row.kapanid === null) {
          return;
        } else {
          return Utility.NeedToShowPacketDetailModal() ? (
            <Button
              sx={{ textDecorationLine: "underline", color: "black" }}
              onClick={() => handlePacketDetailModal(params.value, params.row.kapanid)}
            >
              {params.value}
            </Button>
          ) : (
            <div>{params.value}</div>
          );
        }
      }
    },
    "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
      field: "status",
      headerName: "STATUS",
      headerAlign: "center",
      align: "center",
      width: 100,
      renderCell: renderCellExpand,
    },
    "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
      field: "stonetype",
      headerName: "STONETYPE",
      headerAlign: "center",
      align: "center",
      width: 100,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
      filterOperators: Utility.combinedFilters(),
      valueGetter: (params) => Constants.STONE_TYPE[params.value],
      renderCell: (params) => Constants.STONE_TYPE[params.value],
    },
    "ab3e506e-bddb-456d-b967-922dcee74d56": {
      field: "location",
      headerName: "LOCATION",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 120,
    },
    "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
      field: "size",
      headerName: "SIZE",
      headerAlign: "center",
      align: "center",
      width: 70,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
    },
    "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
      field: "shape",
      headerName: "SHAPE",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 120,
    },
    "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
      field: "color",
      headerName: "COL",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.color.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 60,
    },
    "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
      field: "clarity",
      headerName: "CLARITY",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.clarity.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 75,
    },
    "3597803f-8932-40c6-8de6-81006f4d6d5b": {
      field: "cut",
      headerName: "CUT",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.cut.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 60,
    },
    "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
      field: "pol",
      headerName: "POL",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 60,
    },
    "5541c182-21b8-496e-b342-b0b89f3d302f": {
      field: "sym",
      headerName: "SYM",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.sym.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 60,
    },
    "8296e627-0be5-4a66-830f-ea6867cea137": {
      field: "flo",
      headerName: "FLO",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.flo.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 60,
    },
    "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
      field: "rapnetprice",
      headerName: "ORIGINAL RAP",
      headerAlign: "center",
      align: "center",
      width: 120,
      type: "number",
      valueFormatter: (params) => params.value,
      cellClassName: (params) => 'lavenderCell',
      renderCell: (params) => params.row.rapnetprice
    },
    "798a8663-0f0c-41be-8512-16dd60d5f25e": {
      field: "originaldiscount",
      headerName: "ORIGINAL DIS",
      headerAlign: "center",
      align: "center",
      width: 120,
      type: "number",
      valueFormatter: (params) => params.value,
      cellClassName: (params) => 'lavenderCell',
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.originaldiscount)
    },
    "39c634f1-2644-4307-acbb-983b4de4229e": {
      field: "originalprice",
      headerName: "ORIGINAL P/CT",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 120,
      cellClassName: (params) => 'lavenderCell',
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.originalprice)
    },
    "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
      field: "originaltotal",
      headerName: "ORIGINAL TOTAL",
      headerAlign: "center",
      align: "center",
      width: 120,
      type: "number",
      valueFormatter: (params) => params.value,
      cellClassName: (params) => 'lavenderCell',
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.originaltotal)
    },
    "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
      field: "lab",
      headerName: "LAB",
      headerAlign: "center",
      align: "center",
      width: 60,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.lab.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    "13d48508-b8c9-4661-8a6c-9fb22a3408a3": {
      field: "measurements",
      headerName: "MEASUREMENTS",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 140,
      renderCell: renderCellExpand,
    },
    "567202e6-c31c-4c19-9998-1cacae05af6e": {
      field: "depth",
      headerName: "DEPTH",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 70,
      renderCell: renderCellExpand,
    },
    "f97a0118-3891-4f77-abf7-8636c26d594c": {
      field: "country",
      headerName: "COUNTRY",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "cb7e6add-67ba-4194-ac49-fd0d967db4e8": {
      field: "quality",
      headerName: "QUALITY",
      headerAlign: "center",
      align: "center",
      width: 80,
      renderCell: renderCellExpand,
    },
    "b718f242-4a82-42e7-b98a-40bbf0f67c21": {
      field: "shade",
      headerName: "SHADE",
      headerAlign: "center",
      align: "center",
      width: 60,
      renderCell: renderCellExpand,
    },
    "01e90aca-bdcb-4e8e-80ec-2d7007df8cd3": {
      field: "createdat",
      headerName: "CREATED AT",
      headerAlign: "center",
      align: "center",
      width: 170,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    },
    "3c62ef48-9538-4df7-9484-a9ae1ba31086": {
      field: "height",
      headerName: "HEIGHT",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 70,
      renderCell: renderCellExpand,
    },
    "eed9b7eb-17e4-4ec6-9c9d-c299f885544c": {
      field: "width",
      headerName: "WIDTH",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 70,
      renderCell: renderCellExpand,
    },
    "8eaa8a03-d163-436d-8912-51ea06306f44": {
      field: "certificateid",
      headerName: "CERT",
      headerAlign: "center",
      align: "center",
      width: 100,
      editable: true,
      renderCell: renderCellExpand,
    },
    "77e48fa7-d118-4ab8-865b-9b85b32a004f": {
      field: "fancycolor1",
      headerName: "F.COLOR",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "ccba9873-174e-4060-963a-9893c3625bb2": {
      field: "fancycolorovertone",
      headerName: "OVERTONE",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "c9aa6ee7-30e3-423e-a534-80bb5ef7bbaf": {
      field: "fancycolorintensity",
      headerName: "F.COL.INTENSITY",
      headerAlign: "center",
      align: "center",
      width: 140,
      renderCell: renderCellExpand,
    },
    "706a5cfb-e196-43d1-bbe3-c52aad9a46c1": {
      field: "comment",
      headerName: "COMMENT",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "e7210f4a-6e0a-4591-8428-e6845004ed04": {
      field: "crownheight",
      headerName: "CR.HEIGHT",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "c4e65984-1682-4c1e-812b-8938e9078b2d": {
      field: "crownangle",
      headerName: "CR.ANG",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "0f1de38d-3ae3-4209-a4d5-afe9f3018f22": {
      field: "culetsize",
      headerName: "CULET SZ",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "e3f77521-2c57-46bc-a02b-3c884685d2be": {
      field: "culetcondition",
      headerName: "CULET",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "e5c1f34e-6e1c-4ee8-bb8e-3236716fd1ff": {
      field: "depthpercent",
      headerName: "DEPTH%",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 75,
      renderCell: renderCellExpand,
    },
    "e83c2978-9088-49f0-9dc1-bcced73025fd": {
      field: "gridlemin",
      headerName: "GIRDLEMIN",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "cee57bac-9c19-40d5-b51a-51ed913dbf73": {
      field: "intensitylongtitle",
      headerName: "INTENSITYLONGTITLE",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "06d2a589-0d42-4dde-8724-acf2448c839a": {
      field: "laserinscription",
      headerName: "INS",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
      cellClassName: (params) => Utility.highlightLasertInscriptionCell(params.value)
    },
    "dd934505-1865-48cb-878f-f5d3c18af2d5": {
      field: "note",
      headerName: "NOTE",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "61f8a5f2-8516-419a-b6fd-39ef109cc2f4": {
      field: "paviliondepth",
      headerName: "PV.DEPTH",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "70498a64-1f5f-4f03-8910-a6d7851e4aba": {
      field: "tablepercent",
      headerName: "TABLE%",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 75,
      renderCell: renderCellExpand,
    },
    "db078198-4416-4d53-b3d0-70d3a5418f65": {
      field: "open",
      headerName: "OPEN",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "e9028170-e978-4978-a2c9-1889c1821495": {
      field: "gridlecondition",
      headerName: "GIRDLE COND",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "fe8f55f3-358a-4418-bcdf-c3da4b01db67": {
      field: "gridlepercent",
      headerName: "GIRDLE%",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "daf69bba-bb1f-4d54-9dc7-9383ad429585": {
      field: "pavilionangle",
      headerName: "PV.ANGLE",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "ecfdfc0c-6a25-4a6d-9c8f-3406c23410af": {
      field: "white",
      headerName: "WHITE",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    "24ba0bf8-4ca1-4726-ad90-19a62c8c72e6": {
      field: "boxid",
      headerName: "BOXID",
      headerAlign: "center",
      width: 60,
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: renderCellExpand,
    },
    "cf694b75-ba5d-4a73-8fcf-e7566974c4b6": {
      field: "media",
      headerName: "MEDIA",
      headerAlign: "center",
      align: "center",
      width: 60,
      renderCell: (params) => (
        <Tooltip title={"Stone Media"} sx={{ cursor: "pointer" }}>
          <VideoLibraryTwoToneIcon
            sx={{ cursor: "pointer" }}
            onClick={() => {
              setStoneMediaURL(params.row.stonemediaurl);
              setShowStoneMediaModal(true);
            }} />
        </Tooltip>
      ),
    },
    "f3223657-c8d2-449c-b2f5-5a54c78401ce": {
      field: "tableinc",
      headerName: "TABLE INC",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 120,
    },
    "f114ad2c-8560-4da7-9b03-03ba6bfe39a5": {
      field: "sideinc",
      headerName: "SIDE INC",
      headerAlign: "center",
      align: "center",
      width: 120,
    },
    "e993a4f7-7c50-4d83-abae-fccc03382742": {
      field: "h_and_a",
      headerName: "H&A",
      headerAlign: "center",
      align: "center",
      width: 120,
    },
    "f2fe7666-6489-40ce-b4b7-040c68c88bf3": {
      field: "eyeclean",
      headerName: "EYE CLEAN",
      headerAlign: "center",
      align: "center",
      width: 120,
    },
    "b38b9fdf-bfb8-43c6-82a2-1e366fa71523": {
      field: "tinge",
      headerName: "TINGE",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.tinge.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 120,
    },
    "c95089ee-166d-4783-817c-2638f6d40df1": {
      field: "milky",
      headerName: "MILKY",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.milky.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      width: 120,
    },
    "2a6475ff-481f-48c6-9e92-3ede0b97d7c0": {
      field: "luster",
      headerName: "LUSTER",
      headerAlign: "center",
      align: "center",
      width: 120,
    },
    "556ae429-3605-4df4-88d8-b5223fc2e2fb": {
      field: "tableblack",
      headerName: "TABLE BLACK",
      headerAlign: "center",
      align: "center",
      type: "boolean",
      width: 120,
    },
    "5c70d1ca-2a7b-4660-8160-0beff7ad1150": {
      field: "sideblack",
      headerName: "SIDE BLACK",
      headerAlign: "center",
      align: "center",
      type: "boolean",
      width: 120,
    },
    "a1299782-d545-4d70-bcbc-7d576e396d64": {
      field: "length",
      headerName: "LENGTH",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 120,
    },
    "dfd5488a-b600-4e75-9735-98c26f78dc84": {
      field: "strln",
      headerName: "STAR LENGTH",
      headerAlign: "center",
      align: "center",
      width: 120,
    },
    "66dfbc62-477f-42f0-8097-56c5d8329156": {
      field: "lrhalf",
      headerName: "LR HALF",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 120,
    },
    "e683145c-29dd-4d41-af1a-518ff80090d2": {
      field: "keytosymbols",
      headerName: "KEY TO SYMBOLS",
      headerAlign: "center",
      align: "center",
      width: 120,
    },
    "e32fe363-07cc-4307-82d2-82ae1069b5be": {
      field: "age",
      headerName: "AGE",
      headerAlign: "center",
      align: "center",
      width: 60,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertDateToAge(params.row.age),
    }
  };

  //external fields
  const materialDefaultColumns = [

    {
      headerName: "OFFER ID",
      field: "offerid",
      headerAlign: "center",
      align: "center",
      renderCell: renderCellExpand,
    },
    {
      headerName: "CREATED BY",
      field: "employeeid",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      width: 200,
      valueOptions: filterValues ? filterValues.createdby : [],
      filterOperators: Utility.combinedFilters(),
      valueGetter: (params) => {
        return `${params.row.employeeid} | ${params.row.employeename}`;
      },
      renderCell: (params) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '5px', marginLeft: '10px' }}>
              {`${params.row.employeeid} | ${params.row.employeename}`}
            </span>
          </div>
        );
      },
    },
    {
      headerName: "BROKER COMPANY",
      field: "brokerid",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      width: 200,
      valueOptions: filterValues ? filterValues.broker : [],
      filterOperators: Utility.combinedFilters(),
      valueGetter: (params) => {
        return params.row.brokerid === -1 ? " - " : `${params.row.brokerid} | ${params.row.brokercompanyname}`;
      },
      renderCell: (params) => {
        let displayValue = params.row.brokerid === -1 ? " - " : `${params.row.brokerid} | ${params.row.brokercompanyname}`
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '5px', marginLeft: '10px' }}>
              {displayValue}
            </span>
          </div>
        );
      },
    },
    {
      headerName: "CUSTOMER COMPANY",
      field: "customerid",
      headerAlign: "center",
      align: "center",
      width: 200,
      type: "singleSelect",
      valueOptions: filterValues ? filterValues.customercompanyname : [],
      filterOperators: Utility.combinedFilters(),
      valueGetter: (params) => {
        return `${params.row.customerid} | ${params.row.customercompanyname}`;
      },
      renderCell: (params) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '5px', marginLeft: '10px' }}>
              {`${params.row.customerid} | ${params.row.customercompanyname}`}
            </span>
          </div>
        );
      },
    },
    {
      headerName: "DATE",
      field: "date",
      headerAlign: "center",
      align: "center",
      type: "date",
      width: 120,
      valueGetter: (params) => {
        if (params.row.createdat) {
          return new Date(params.row.createdat);
        }
        return null;
      },
      renderCell: (params) => {
        if (params.row.createdat) {
          return Utility.ConvertUtcToLocalDate(params.row.createdat);
        } else {
          return "";
        }
      }
    },
    {
      headerName: "TOTAL CTS",
      field: "totalSize",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 80,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value)
    },
    {
      headerName: "AVG PRICE",
      field: "avgPrice",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 120,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value)
    },
    {
      headerName: "AVG DIS",
      field: "avgDiscount",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 70,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value)
    },
    {
      headerName: "TOTAL PRICE",
      field: "totalPrice",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      width: 120,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value)
    },
    {
      headerName: "REMARK",
      field: "remark",
      headerAlign: "center",
      align: "center",
      width: 120,
      renderCell: renderCellExpand,
    },
  ];

  const materialColumns = materialDefaultColumns

  const getDetailPanelHeight = React.useCallback(() => 'auto', []);

  const getDetailPanelContent = React.useCallback(
    (row, mainData) => <DetailPanelContent row={row} mainData={mainData} />,
    [columnNames],
  );

  function DetailPanelContent({ row: rowProp, mainData }) {
    let row = mainData[rowProp.offerid];
    return (
      <Stack
        sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
        direction="column"
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            color: '#000',
            width: "87vw",
            position: "relative",
            "& .MuiTablePagination-selectLabel": {
              marginTop: "15px",
            },
            "& .MuiTablePagination-displayedRows": {
              marginTop: "15px",
            },
            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
              fontWeight: Constants.defaultFontWeight,
            },
            "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
              color: "#1976d2",
              padding: "4px 5px",
              borderRadius: "4px",
            },
            "& .css-cc8tf1": {
              fontWeight: Constants.defaultFontWeight,
            },
            "& .lavenderCell": {
              color: "#080808",
              backgroundColor: "#D8BFD8",
            },
            height: 'auto',
            marginLeft: '16px',
          }}
        >
          <DataGridPro
            disableRowSelectionOnClick={false}
            density="compact"
            columns={columnNames}
            rows={row}
            getRowId={(row) => row.stoneid}
            sx={{
              '& .MuiDataGrid-columnHeader': {
                color: '#fff',
                backgroundColor: 'grey',
              },
              "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                py: "8px",
              },
              "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                py: "15px",
              },
              "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                py: "22px",
              },

              fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
              maxHeight: '30vh',
            }}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  // Hide columns id, the other columns will remain visible
                  locationid: false,
                  kapanid: false,
                  country: false,
                  quality: false,
                  shade: false,
                  repacode: false,
                  createdat: false,
                  height: false,
                  width: false,
                  packetid: false,
                  crownheight: false,
                  culetsize: false,
                  culetcondition: false,
                  gridlemin: false,
                  intensitylongtitle: false,
                  laserinscription: false,
                  note: false,
                  paviliondepth: false,
                  gridlecondition: false,
                  gridlepercent: false,
                  pavilionangle: false,
                  white: false,
                  status: false,
                },
              },
              pagination: {
                paginationModel: Constants.pageSize,
              },
            }}
            pageSizeOptions={Constants.pageSizeOptions}
            pagination
          />
        </Box>
      </Stack>
    );
  }

  function CustomToolbar() {

    const [exportMenu, setExportMenu] = useState(undefined);

    const buttonMinWidth = Utility.isReadAllowed(fullExcelPermission) ? "130px" : "100px";
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          alignItems: "center",
          marginHorizontal: "5px",
          "&.MuiDataGrid-toolbarContainer": {
            padding: "0px 4px !important",
          },
        }}
      >
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarExport />
        <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
        {offerLoader ? (
          <CircularProgress size="1rem" />
        ) : (
          <Tooltip title="Reload List">
            <RefreshOutlinedIcon
              onClick={() => getAllCustomerData()}
              color="primary"
              fontSize="medium"
              sx={{ cursor: "pointer", mt: 1 }}
            />
          </Tooltip>
        )}

        <LoadingButton
          id="demo-customized-export-menu"
          aria-controls={
            exportMenu ? "demo-customized-export-menu" : undefined
          }
          aria-haspopup="true"
          aria-expanded={exportMenu ? "true" : undefined}
          variant="outlined"
          disableElevation
          disabled={listData.length === 0}
          onClick={(e) => setExportMenu(e.currentTarget)}
          sx={{
            color: "#2e7d32",
            borderRadius: "4px",
            border: "1px solid rgba(46, 125, 50, 0.5)",
            padding: "2px 2px 2px 8px",
            cursor: "pointer",
          }}
          loadingPosition="start"
          startIcon={<TbTableExport size={18} />}
        >
          <span style={{ fontSize: "14px", marginLeft: "4px" }}>EXPORT</span>
          <KeyboardArrowDownIcon />
        </LoadingButton>

        <StyledMenuButtons
          id="demo-customized-export-menu"
          MenuListProps={{
            "aria-labelledby": "demo-customized-export-menu",
            style: {
              maxHeight: 250,
            },
          }}
          anchorEl={exportMenu}
          open={exportMenu}
          onClose={() => setExportMenu(null)}
        >
          <MenuItem>
            {Utility.isReadAllowed(fullExcelPermission) &&
              <Button
                component="label"
                variant="outlined"
                size="small"
                color="secondary"
                sx={{
                  ml: "5px",
                  minWidth: buttonMinWidth,
                  justifyContent: "flex-start",
                  textAlign: "left",
                  "& .MuiButton-startIcon": {
                    marginRight: "1px",
                    marginLeft: "0px",
                  },
                }}
                startIcon={<Download />}
                onClick={() => {
                  getExcelData(true);
                  setExportMenu(null);
                }}
              >
                FULL EXCEL
              </Button>
            }
          </MenuItem>
          <MenuItem>
            <Button
              component="label"
              variant="outlined"
              size="small"
              color="secondary"
              sx={{
                ml: "5px",
                minWidth: buttonMinWidth,
                justifyContent: "flex-start",
                textAlign: "left",
                "& .MuiButton-startIcon": {
                  marginRight: "1px",
                  marginLeft: "0px",
                },
              }}
              startIcon={<Download />}
              onClick={() => {
                getExcelData(false);
                setExportMenu(null);
              }}
            >
              EXCEL
            </Button>
          </MenuItem>
        </StyledMenuButtons>
        <GridToolbarQuickFilter debounceMs={500} />
      </GridToolbarContainer>
    )
  }

  return (
    <div>
      {showStoneDetailModal ? (
        <StoneDetailModal
          show={showStoneDetailModal}
          stoneId={stoneId}
          handleClose={handleClose}
        />
      ) : null}

      {showKapanDetailModal ? (
        <KapanHistoryModal
          show={showKapanDetailModal}
          kapanId={kapanId}
          close={() => setShowKapanDetailModal(false)}
        />
      ) : null}

      {showSuccessSnackbar ? (
        <CommonSnackbar
          show={true}
          message={successMessage}
          variant={snackbarVariant}
          autoHide={Constants.SnackBarCloseTimer}
          title={snackbarVariant === "success" ? "Success" : "Failed"}
          handleClose={handleClose}
        />
      ) : null}

      {showStoneMediaModal ? (
        <StoneMediaModal
          show={showStoneMediaModal}
          stoneMediaURL={stoneMediaURL}
          handleClose={handleClose}
        />
      ) : null}

      {showPacketDetailModal ? (
        <PacketHistoryModal
          show={showPacketDetailModal}
          packetId={packetId}
          kapanId={kapanId}
          close={handleClose}
        />
      ) : null}

      {showUserDetailModal ? (
        <UserDetailModal
          show={true}
          handleClose={() => setShowUserDetailModal(false)}
          handleSelect={(selectedOption) => {
            const selectedCustomers = customerData.customerOptions.filter(opt =>
              selectedOption.includes(opt.value)
            );
            setCustomerData({
              ...customerData,
              customer: selectedCustomers,
            });
            setShowUserDetailModal(false)
          }}
          userids={customerData.customerOptions.filter(option => option.value !== "*").map(cust => cust.value)}
          selectedIds={customerData.customer.length !== 0 ? customerData.customer.map(iCustomer => iCustomer.value) : []}
          usertype={"C"}
          loading={showUserDetailModal}
          isMulti={true}
        />
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "flex-start",
          width: "100%",
          marginBottom: 1,
          gap: "15px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "15px",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography sx={{ whiteSpace: "nowrap" }}>Company Name</Typography>

          {Constants.loggedInUser.usertype !== "C" ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "15px",
                }}
              >
                <Box
                  sx={{
                    width: { xs: "100%", sm: "25rem" },
                    minWidth: "200px",
                  }}
                >
                  <Select
                    menuPlacement="auto"
                    blurInputOnSelect={false}
                    menuPortalTarget={document.body}
                    styles={Constants.Styles}
                    closeMenuOnSelect={false}
                    isMulti
                    options={customerData.customerOptions}
                    value={customerData.customer}
                    onChange={(e) => setCustomerData({ ...customerData, customer: e })}
                  />
                </Box>

                <Tooltip title="View More Data">
                  <span style={{ cursor: "pointer" }}>
                    <OpenInNewIcon
                      fontSize="medium"
                      color="action"
                      onClick={() => setShowUserDetailModal(true)}
                    />
                  </span>
                </Tooltip>

                <Box
                  sx={{
                    "& .Mui-disabled": {
                      backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                      color: "rgba(0, 0, 0, 0.26) !important",
                    },
                  }}
                >
                  <LoadingButton
                    variant="contained"
                    onClick={() => handleSearch()}
                    startIcon={<SearchOutlinedIcon size="large" />}
                    loading={offerLoader}
                    loadingPosition="start"
                  >
                    Search
                  </LoadingButton>
                </Box>
              </Box>
            </>
          ) : (
            <Typography fontWeight={Constants.defaultFontWeightBold} variant="h6">
              {Constants.loggedInUser.companyname}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: '#fff',
          color: '#000',
          width: "93.5vw",
          position: "relative",
          "& .MuiTablePagination-selectLabel": {
            marginTop: "15px",
          },
          "& .MuiTablePagination-displayedRows": {
            marginTop: "15px",
          },
          "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
            fontWeight: Constants.defaultFontWeight,
          },
          "& .css-cc8tf1": {
            fontWeight: Constants.defaultFontWeight,
          },
          "& .lavenderCell": {
            color: "#080808",
            backgroundColor: "#D8BFD8",
          },
          height: "76vh",
        }}
      >
        <DataGridPro
          disableRowSelectionOnClick={false}
          density="compact"
          columns={materialColumns}
          rows={listData}
          loading={offerLoader}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              color: '#fff',
              backgroundColor: 'grey',
            },
            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
              py: "4px",
            },
            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
              py: "12px",
            },
            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
              py: "18px",
            },

            '& .MuiDataGrid-row': {
              whiteSpace: 'nowrap',
            },
            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
          }}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          getRowId={(row) => row.offerid}
          initialState={{
            pagination: {
              paginationModel: Constants.pageSize,
            },
          }}
          pageSizeOptions={Constants.pageSizeOptions}
          pagination
          getDetailPanelHeight={getDetailPanelHeight}
          getDetailPanelContent={({ row }) => getDetailPanelContent(row, mainData)}
        />
      </Box>
    </div>
  );
};

export default Offer;
